import React, { createContext, useContext, useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import axios from "axios";

const AuthContext = createContext(null);
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => { try { return localStorage.getItem("nd_token"); } catch { return null; } });
  const [loading, setLoading] = useState(true);
  // Set to `true` immediately after login/signup so the token-changed effect
  // does NOT re-fetch /auth/me (we already have the user from the login response).
  const skipNextFetchRef = React.useRef(false);

  // Global 401 handler — any authenticated request that comes back "Session
  // expired" (JWT older than the user's current pwd_epoch) means an admin
  // reset or password change nuked this session. Wipe local state and let
  // the router redirect to landing so the user is forced to re-authenticate.
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      r => r,
      err => {
        const detail = err?.response?.data?.detail || "";
        const status = err?.response?.status;
        if (status === 401 && /session expired|invalid token|user not found/i.test(detail)) {
          if (localStorage.getItem("nd_token")) {
            localStorage.removeItem("nd_token");
            delete axios.defaults.headers.common["Authorization"];
            setToken(null); setUser(null);
            try { window.dispatchEvent(new CustomEvent("nd-session-expired")); } catch { /* no-op */ }
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      setLoading(false);
      return;
    }
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      axios.get(`${API}/auth/me`)
        .then(r => setUser(r.data))
        .catch(() => { setToken(null); localStorage.removeItem("nd_token"); })
        .finally(() => setLoading(false));
    } else {
      delete axios.defaults.headers.common["Authorization"];
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const r = await axios.post(`${API}/auth/login`, { email, password });
    axios.defaults.headers.common["Authorization"] = `Bearer ${r.data.token}`;
    localStorage.setItem("nd_token", r.data.token);
    skipNextFetchRef.current = true;
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };
  const signup = async (email, password, display_name) => {
    const r = await axios.post(`${API}/auth/signup`, { email, password, display_name });
    axios.defaults.headers.common["Authorization"] = `Bearer ${r.data.token}`;
    localStorage.setItem("nd_token", r.data.token);
    skipNextFetchRef.current = true;
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  };
  const setAuthFromReset = (jwt, u) => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${jwt}`;
    localStorage.setItem("nd_token", jwt);
    skipNextFetchRef.current = true;
    setToken(jwt);
    setUser(u);
  };
  const logout = () => {
    localStorage.removeItem("nd_token");
    setToken(null); setUser(null);
    delete axios.defaults.headers.common["Authorization"];
  };
  const refreshUser = async () => {
    if (!token) return;
    const r = await axios.get(`${API}/auth/me`);
    setUser(r.data);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, signup, logout, refreshUser, setAuthFromReset, API }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
